// The company's monthly reports, shaped for the Sources page period dropdown.
// Reuses the same `getReports` the dashboard uses (so the month list matches
// the report detail's selector exactly), but keeps only real completed reports
// — a month with no finished report has no citations to show.

import { useQuery } from "@tanstack/react-query";
import { projectsInstance } from "@/services/ProjectsService";
import { useAuth } from "@/context/AuthContext";
import { vbKeys, useVbWorkspaceScope } from "@/hooks/vb/useBuyers";

export interface ReportPeriod {
  id: number;
  label: string; // "March 2026"
  created_ts: string;
}

export function useReportPeriods(projectId: string | undefined) {
  const { token } = useAuth();
  const { wsKey, wsReady } = useVbWorkspaceScope();
  return useQuery<ReportPeriod[]>({
    queryKey: [...vbKeys.reportPeriods(projectId ?? ""), { ws: wsKey }],
    enabled: !!projectId && !!token && wsReady,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { results } = await projectsInstance.getReports(token, projectId);
      const sorted = (results ?? [])
        .filter((r) => r.status === "completed" && r.created_ts)
        .map((r) => {
          const d = new Date(r.created_ts);
          return {
            id: r.id,
            created_ts: r.created_ts,
            label: d.toLocaleString("default", { month: "long", year: "numeric" }),
          };
        })
        .sort((a, b) => new Date(b.created_ts).getTime() - new Date(a.created_ts).getTime());
      // A report is a per-calendar-month snapshot, so collapse any same-month
      // duplicates (keep the newest) — matches the dashboard's month selector.
      const seen = new Set<string>();
      return sorted.filter((p) => (seen.has(p.label) ? false : (seen.add(p.label), true)));
    },
  });
}
